/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import { useUnits } from '@web-stories-wp/units';
import { Icons, VisuallyHidden } from '@web-stories-wp/design-system';
import { __, sprintf } from '@web-stories-wp/i18n';

/**
 * Internal dependencies
 */
import { getDefinitionForType, getLayerName } from '../../elements';
import { useStory, useTransform, useCanvas, useDropTargets } from '../../app';
import {
  elementWithPosition,
  elementWithSize,
  elementWithRotation,
} from '../../elements/shared';
import WithMask from '../../masks/frame';
import WithLink from '../elementLink/frame';
import { useTransformHandler } from '../transform';
import useDoubleClick from '../../utils/useDoubleClick';
import { isTextBoxAttachment } from '../../utils/textBoxAttachment';

// @todo: should the frame borders follow clip lines?

// Pointer events are disabled in the display mode to ensure that selection
// can be limited to the mask.
const Wrapper = styled.div`
  ${elementWithPosition}
  ${elementWithSize}
	${elementWithRotation}

  outline: 1px solid transparent;
  transition: outline-color 0.5s;

  &:focus,
  &:active {
    outline-color: ${({ theme, hasMask }) =>
      hasMask ? 'transparent' : theme.colors.border.selection};
  }

  ${({ isLocked, hasMask, theme }) =>
    !isLocked &&
    !hasMask &&
    `
    &:hover {
      outline-color: ${theme.colors.border.selection};
    }

    &[data-moveable-hovered='true'] {
      outline-color: ${theme.colors.border.selection};
    }
  `}

  ${({ isClickable }) => !isClickable && `pointer-events: none;`}
`;

const EmptyFrame = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const PinnedStatus = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.colors.border.selection};
  cursor: help;
  user-select: none;

  svg {
    flex: 0 0 auto;
    width: 12px;
    height: 12px;
  }
`;

const NOOP = () => {};

function FrameElement({ id }) {
  const {
    setEditingElement,
    setNodeForElement,
    handleSelectElement,
    isEditing,
  } = useCanvas((state) => ({
    setEditingElement: state.actions.setEditingElement,
    setNodeForElement: state.actions.setNodeForElement,
    handleSelectElement: state.actions.handleSelectElement,
    isEditing: state.state.isEditing,
  }));

  const {
    draggingResource,
    activeDropTargetId,
    isDropSource,
    registerDropTarget,
    unregisterDropTarget,
  } = useDropTargets(
    ({
      state: { draggingResource, activeDropTargetId },
      actions: { isDropSource, registerDropTarget, unregisterDropTarget },
    }) => ({
      draggingResource,
      activeDropTargetId,
      isDropSource,
      registerDropTarget,
      unregisterDropTarget,
    })
  );

  const {
    element,
    isSelected,
    isOnlySelectedElement,
    isBackground,
    pinnedTargetLabel,
  } = useStory(({ state }) => {
    const elements = state.currentPage?.elements || [];
    const currentElement = elements.find((el) => el.id === id);
    const attachment = isTextBoxAttachment(currentElement?.textBoxAttachment)
      ? currentElement.textBoxAttachment
      : null;
    const target = attachment
      ? elements.find(({ id: elementId }) => elementId === attachment.targetId)
      : null;
    const isSelected = state.selectedElementIds.includes(id);
    const isOnlySelectedElement =
      isSelected && state.selectedElementIds.length === 1;

    return {
      isSelected,
      isBackground: elements[0]?.id === id,
      element: currentElement,
      isOnlySelectedElement,
      pinnedTargetLabel: target
        ? String(getLayerName(target) || '').trim() ||
          __('Untitled layer', 'web-stories')
        : null,
    };
  });

  const { type, flip, isLocked, isHidden } = element;
  const pinnedStatusLabel = pinnedTargetLabel
    ? sprintf(__('Pinned to %s', 'web-stories'), pinnedTargetLabel)
    : null;
  const pinnedStatusId = `pinned-status-${id}`;

  // Unlocked elements are always clickable,
  // locked elements are only clickable if selected,
  // hidden elements are never clickable.
  const isClickable = (!isLocked || isSelected) && !isHidden;
  const { Frame, isMaskable, Controls } = getDefinitionForType(type);
  const elementRef = useRef();
  const [hovering, setHovering] = useState(false);

  const isAnythingTransforming = useTransform(
    ({ state }) => !isSelected && hovering && !state.isAnythingTransforming
  );

  const { getBox } = useUnits((state) => ({
    getBox: state.actions.getBox,
  }));

  useLayoutEffect(() => {
    setNodeForElement(id, elementRef.current);
  }, [id, setNodeForElement]);
  const box = getBox(element);

  const [isTransforming, setIsTransforming] = useState(false);

  useTransformHandler(id, (transform) => {
    const target = elementRef.current;
    if (transform === null) {
      target.style.transform = '';
      target.style.width = '';
      target.style.height = '';
    } else {
      const hasGeometry =
        transform.translate ||
        typeof transform.rotate !== 'undefined' ||
        transform.resize;
      const { translate = [0, 0], rotate, resize } = transform;
      if (hasGeometry) {
        const rotation =
          typeof rotate === 'number' ? rotate : box.rotationAngle || 0;
        target.style.transform = `translate(${translate[0]}px, ${translate[1]}px) rotate(${rotation}deg)`;
        if (resize && resize[0] !== 0 && resize[1] !== 0) {
          target.style.width = `${resize[0]}px`;
          target.style.height = `${resize[1]}px`;
        }
      }
    }
    if (transform?.dropTargets?.hover !== undefined) {
      target.style.opacity = transform.dropTargets.hover ? 0 : 1;
    }
    setIsTransforming(transform !== null);
  });

  const isMuted = useMemo(
    () => Boolean(element?.resource?.isMuted),
    [element?.resource?.isMuted]
  );

  useEffect(() => {
    const videoEl = document.getElementById(`video-${id}`);

    if (!videoEl) {
      return;
    }

    videoEl.muted = isMuted;
  }, [id, isMuted]);

  // Media needs separate handler for double click.
  const { isMedia } = getDefinitionForType(type);
  const handleMediaDoubleClick = useCallback(
    (evt) => {
      if (!isSelected) {
        handleSelectElement(id, evt);
      }
      setEditingElement(id);
    },
    [id, setEditingElement, handleSelectElement, isSelected]
  );
  const handleMediaClick = useDoubleClick(NOOP, handleMediaDoubleClick);

  const eventHandlers = useMemo(
    () => ({
      onMouseDown: (evt) => {
        if (!isSelected) {
          handleSelectElement(id, evt);
        }
        elementRef.current.focus({ preventScroll: true });
        if (!isBackground) {
          evt.stopPropagation();
        }
      },
      onFocus: (evt) => {
        if (!isSelected) {
          handleSelectElement(id, evt);
        }
      },
      onPointerEnter: () => setHovering(true),
      onPointerLeave: () => setHovering(false),
      onClick: isMedia ? handleMediaClick(id) : null,
    }),
    [
      handleMediaClick,
      handleSelectElement,
      id,
      isBackground,
      isSelected,
      isMedia,
      setHovering,
    ]
  );

  return (
    <WithLink
      element={element}
      active={isAnythingTransforming && !isLocked}
      anchorRef={elementRef}
    >
      {Controls && (
        <Controls
          isTransforming={isTransforming}
          isSelected={isSelected}
          isSingleElement={isOnlySelectedElement}
          isEditing={isEditing}
          box={box}
          elementRef={elementRef}
          element={element}
        />
      )}
      <Wrapper
        ref={elementRef}
        data-element-id={id}
        data-is-clickable={isClickable ? 'true' : 'false'}
        {...box}
        // Needed for being able to focus on the selected element on canvas, e.g. for entering edit mode.
        // eslint-disable-next-line styled-components-a11y/no-noninteractive-tabindex
        tabIndex={0}
        aria-labelledby={`layer-${id}`}
        aria-describedby={
          isSelected && pinnedStatusLabel ? pinnedStatusId : undefined
        }
        hasMask={isMaskable}
        isClickable={isClickable}
        data-testid="frameElement"
        {...eventHandlers}
      >
        {isSelected && pinnedStatusLabel && (
          <PinnedStatus id={pinnedStatusId} title={pinnedStatusLabel}>
            <Icons.Pin aria-hidden="true" />
            <VisuallyHidden>{pinnedStatusLabel}</VisuallyHidden>
          </PinnedStatus>
        )}
        <WithMask
          element={element}
          fill
          flip={flip}
          draggingResource={draggingResource}
          activeDropTargetId={activeDropTargetId}
          isDropSource={isDropSource}
          registerDropTarget={registerDropTarget}
          unregisterDropTarget={unregisterDropTarget}
          isSelected={isSelected}
        >
          {Frame ? (
            <Frame wrapperRef={elementRef} element={element} box={box} />
          ) : (
            <EmptyFrame />
          )}
        </WithMask>
      </Wrapper>
    </WithLink>
  );
}

FrameElement.propTypes = {
  id: PropTypes.string.isRequired,
};

export default FrameElement;
